import { useEffect, useRef, useCallback, useState } from "react";
import { apiRequest } from "../../api/client";
import { getApiBase } from "../utils/urlHelpers";
import { isAddressAutocompleteServerEnabled } from "../utils/addressAutocompleteEnabled";
import { getAddressAutocompleteDropdownMountEl, applyAddressSuggestDropdownTheme } from "../utils/addressAutocompleteDropdownMount";
import { SMARTY_SUGGEST_INPUT_MIN_CHARS, suggestSearchFromInputValue, commitPayloadFromVerify, appendSmartyEmptyState, fillSmartySuggestionButton } from "../utils/smartyAddressAutocompleteUi.js";
import { ADDRESS_AC_DROPDOWN_CLASS as DROPDOWN_CLASS, ADDRESS_AC_ITEM_CLASS as ITEM_CLASS } from "../addressSuggestDropdownStyles.js";
import { buildSmartySelectedParam } from "@shared/smarty.js";
function useLatestRef(value) {
  const r = useRef(value);
  r.current = value;
  return r;
}
export function isAddressAutocompleteDebugEnabled() {
  try {
    if (typeof window === "undefined") return false;
    if (window.NM_DEBUG_ADDRESS_AC === true) return true;
    const ls = window.localStorage?.getItem("nmDebugAddressAc");
    if (ls === "1" || ls === "true") return true;
    const q = new URLSearchParams(window.location?.search || "");
    if (q.get("nmDebugAc") === "1" || q.get("nmDebugAc") === "true") return true;
  } catch (_) {}
  return false;
}
export function printAddressAutocompleteDebugHelp() {
  console.log(`[nm-address-ac] How to enable verbose logs:
  localStorage.setItem("nmDebugAddressAc","1"); location.reload()
  Or add ?nmDebugAc=1 to the URL.

Quick checks in console:
  window.nitromuleReactData?.addressAutocompleteAvailable   // must be truthy "1" (or 1/true) for lookups
  (from /api/config when server has SMARTY_AUTH_ID + SMARTY_AUTH_TOKEN)

If Network shows 200 but no dropdown: open the response body — need suggestions[] and not available:false.
Also look for BLOCKED: response_dropped_stale_seq (a newer request replaced this one while typing).

Then type at least 2 characters in a street field and watch [nm-address-ac] lines.`);
}
function acDebug(...args) {
  try {
    if (isAddressAutocompleteDebugEnabled()) {
      console.log("[nm-address-ac]", performance.now().toFixed(0) + "ms", ...args);
    }
  } catch (_) {}
}
function acBlocked(code, hint, extra = {}) {
  try {
    if (!isAddressAutocompleteDebugEnabled()) return;
    console.warn(`[nm-address-ac] BLOCKED: ${code}`, {
      hint,
      ...extra
    });
  } catch (_) {}
}
function configSnapshot() {
  const d = typeof window !== "undefined" ? window.nitromuleReactData : null;
  return {
    addressAutocompleteAvailable: d?.addressAutocompleteAvailable,
    hasnitromuleReactData: !!d,
    apiBase: typeof window !== "undefined" ? getApiBase() : ""
  };
}
const DEBOUNCE_MS = 200;
const MIN_CHARS = SMARTY_SUGGEST_INPUT_MIN_CHARS;
const POLL_MS = 150;
function positionDropdown(dd, input) {
  const r = input.getBoundingClientRect();
  dd.style.position = "fixed";
  dd.style.top = `${Math.min(r.bottom + 4, window.innerHeight - 8)}px`;
  dd.style.left = `${Math.max(8, Math.min(r.left, window.innerWidth - 320))}px`;
  dd.style.width = `${Math.min(Math.max(r.width, 280), window.innerWidth - 16)}px`;
  dd.style.maxWidth = `${window.innerWidth - 16}px`;
}
function isAutocompleteActive() {
  return isAddressAutocompleteServerEnabled();
}
export function useAddressAutocompleteAvailable() {
  const [on, setOn] = useState(() => typeof window !== "undefined" && isAddressAutocompleteServerEnabled());
  useEffect(() => {
    const sync = () => {
      const next = isAddressAutocompleteServerEnabled();
      acDebug("useAddressAutocompleteAvailable sync", {
        next,
        ...configSnapshot()
      });
      setOn(next);
    };
    sync();
    window.addEventListener("nitromule-config", sync);
    return () => window.removeEventListener("nitromule-config", sync);
  }, []);
  return on;
}
export default function useAddressAutocomplete(inputRef, {
  onAddressSelected,
  disabled = false
}) {
  const onAddressSelectedRef = useLatestRef(onAddressSelected);
  const instanceRef = useRef(`ac-${Math.random().toString(36).slice(2, 9)}`);
  const ddRef = useRef(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const seqRef = useRef(0);
  const inputMountedRef = useRef(true);
  const id = instanceRef.current;
  const closeDropdown = useCallback(() => {
    if (ddRef.current?._cleanup) ddRef.current._cleanup();
    ddRef.current?.remove();
    ddRef.current = null;
  }, []);
  /** Smarty suggestions are already USPS-validated; commit without a second verify pass. */
  const verifyAndCommit = useCallback(s => {
    onAddressSelectedRef.current?.(commitPayloadFromVerify(null, s));
    closeDropdown();
  }, [closeDropdown]);
  const fetchLookup = useCallback((searchRaw, selected, input) => {
    const search = suggestSearchFromInputValue(searchRaw);
    if (!search && !selected) {
      closeDropdown();
      return;
    }
    if (selected) closeDropdown();
    const mySeq = ++seqRef.current;
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    let url = `nitromule/v1/address-suggest?q=${encodeURIComponent(search)}`;
    if (selected) url += `&selected=${encodeURIComponent(selected)}`;
    const t0 = typeof performance !== "undefined" ? performance.now() : 0;
    const requestUrl = `${getApiBase().replace(/\/$/, "")}/${url.replace(/^\//, "")}`;
    acDebug(id, "fetchLookup START", {
      seq: mySeq,
      q: search.slice(0, 40),
      selected: !!selected,
      requestUrl
    });
    apiRequest(url, {
      noAuth: true,
      signal: abortRef.current.signal,
      cache: "no-store"
    }).then(async r => {
      const elapsed = typeof performance !== "undefined" ? (performance.now() - t0).toFixed(0) : "?";
      const text = await r.text();
      let data = {};
      try {
        if (!text || !String(text).trim()) {
          data = {};
        } else {
          data = JSON.parse(text);
        }
      } catch (parseErr) {
        acBlocked("suggest_not_json", "address-suggest response was not JSON — check Network tab (HTML error page, wrong host, or blocked path).", {
          status: r.status,
          preview: String(text).slice(0, 200),
          requestUrl
        });
        acDebug(id, "fetchLookup JSON parse error", parseErr?.message || parseErr, {
          status: r.status
        });
        closeDropdown();
        return null;
      }
      if (!r.ok) {
        acBlocked("suggest_http_error", `address-suggest returned HTTP ${r.status}`, {
          status: r.status,
          body: data,
          requestUrl
        });
        closeDropdown();
        return null;
      }
      return {
        data,
        elapsed
      };
    }).then(parsed => {
      if (!parsed) return;
      const {
        data,
        elapsed
      } = parsed;
      if (mySeq !== seqRef.current) {
        acBlocked("response_dropped_stale_seq", "Network may show 200 OK, but this response was discarded because a newer address-suggest started (typing/debounce). Compare seq in logs.", {
          mySeq,
          currentSeq: seqRef.current,
          elapsedMs: elapsed
        });
        acDebug(id, "fetchLookup DROP stale seq", {
          mySeq,
          currentSeq: seqRef.current,
          elapsedMs: elapsed
        });
        return;
      }
      if (!inputMountedRef.current) {
        acBlocked("response_dropped_unmounted", "200 OK arrived after the autocomplete hook cleaned up (modal closed or input unmounted).", {
          elapsedMs: elapsed
        });
        acDebug(id, "fetchLookup DROP unmounted", {
          elapsedMs: elapsed
        });
        return;
      }
      const list = Array.isArray(data.suggestions) ? data.suggestions : [];
      const serverSaysOff = data.available === false || data.available === "false" || data.available === 0;
      acDebug(id, "fetchLookup RESPONSE", {
        elapsedMs: elapsed,
        available: data.available,
        availableStrictFalse: serverSaysOff,
        suggestionCount: list.length,
        upstream: data.upstream,
        rawKeys: data && typeof data === "object" ? Object.keys(data) : []
      });
      if (serverSaysOff) {
        acBlocked("server_available_false", "address-suggest JSON had available:false — Smarty not configured on server for this route, or error path returned that flag.", {
          requestUrl,
          response: data
        });
        acDebug(id, "no dropdown: available===false");
        closeDropdown();
        return;
      }
      if (!list.length) {
        acBlocked("empty_suggestions", "Smarty returned no rows for this query — try more of the street address, or check upstream (upstream field in logs if present).", {
          qLen: search.length,
          upstream: data.upstream
        });
        acDebug(id, "dropdown: empty suggestions list");
        if (selected) {
          closeDropdown();
          return;
        }
        closeDropdown();
        const emptyDd = document.createElement("div");
        emptyDd.className = DROPDOWN_CLASS;
        applyAddressSuggestDropdownTheme(emptyDd, input);
        emptyDd.setAttribute("role", "status");
        appendSmartyEmptyState(emptyDd);
        const mountEl = getAddressAutocompleteDropdownMountEl(input) || document.body;
        mountEl.appendChild(emptyDd);
        ddRef.current = emptyDd;
        positionDropdown(emptyDd, input);
        const repositionEmpty = () => {
          if (ddRef.current === emptyDd && inputRef.current) positionDropdown(emptyDd, inputRef.current);
        };
        window.addEventListener("scroll", repositionEmpty, true);
        window.addEventListener("resize", repositionEmpty);
        emptyDd._cleanup = () => {
          window.removeEventListener("scroll", repositionEmpty, true);
          window.removeEventListener("resize", repositionEmpty);
        };
        return;
      }
      closeDropdown();
      const dd = document.createElement("div");
      dd.className = DROPDOWN_CLASS;
      applyAddressSuggestDropdownTheme(dd, input);
      dd.setAttribute("role", "listbox");
      list.forEach(s => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = ITEM_CLASS;
        fillSmartySuggestionButton(btn, s, { itemClass: ITEM_CLASS });
        btn.addEventListener("mousedown", e => e.preventDefault());
        btn.addEventListener("click", () => {
          const sel = buildSmartySelectedParam(s);
          if (sel) {
            fetchLookup((input.value || "").trim(), sel, input);
          } else {
            verifyAndCommit(s);
          }
        });
        dd.appendChild(btn);
      });
      const mountEl = getAddressAutocompleteDropdownMountEl(input) || document.body;
      mountEl.appendChild(dd);
      ddRef.current = dd;
      positionDropdown(dd, input);
      const reposition = () => {
        if (ddRef.current === dd && inputRef.current) positionDropdown(dd, inputRef.current);
      };
      window.addEventListener("scroll", reposition, true);
      window.addEventListener("resize", reposition);
      dd._cleanup = () => {
        window.removeEventListener("scroll", reposition, true);
        window.removeEventListener("resize", reposition);
      };
      try {
        const ir = input.getBoundingClientRect();
        const dr = dd.getBoundingClientRect();
        acDebug(id, "dropdown mounted", {
          items: list.length,
          inputRect: {
            top: ir.top,
            left: ir.left,
            w: ir.width,
            h: ir.height
          },
          dropdownRect: {
            top: dr.top,
            left: dr.left,
            w: dr.width,
            h: dr.height
          }
        });
      } catch (_) {
        acDebug(id, "dropdown mounted", {
          items: list.length
        });
      }
    }).catch(err => {
      const aborted = err?.name === "AbortError" || abortRef.current?.signal?.aborted;
      if (!aborted) {
        acBlocked("fetch_network_error", "Request to address-suggest failed (offline, CORS, blocked, or server down).", {
          message: err?.message || String(err),
          requestUrl
        });
      }
      acDebug(id, "fetchLookup ERROR", {
        aborted,
        message: err?.message || String(err)
      });
    });
  }, [closeDropdown, verifyAndCommit, inputRef]);
  useEffect(() => {
    acDebug(id, "effect run", {
      disabled,
      ...configSnapshot()
    });
    if (disabled) {
      acBlocked("hook_disabled", "This input passed disabled=true to useAddressAutocomplete (e.g. Smarty off in config for AddressFormModal).", {
        instanceId: id,
        ...configSnapshot()
      });
      acDebug(id, "SKIP: hook disabled");
      return () => {
        inputMountedRef.current = false;
        closeDropdown();
      };
    }
    inputMountedRef.current = true;
    let boundEl = null;
    let pollId = null;
    let pollTicks = 0;
    const targetElement = e => {
      const n = e.target;
      if (n && n.nodeType === 1) return n;
      if (n && n.parentElement) return n.parentElement;
      return null;
    };
    const onDocMouseDown = e => {
      if (!ddRef.current) return;
      const t = targetElement(e);
      if (!t || typeof t.closest !== "function") {
        closeDropdown();
        return;
      }
      if (t.closest(`.${DROPDOWN_CLASS}`)) {
        if (!t.closest(`.${ITEM_CLASS}`)) e.preventDefault();
        return;
      }
      const input = inputRef.current;
      if (input && (input === t || input.contains(t))) return;
      closeDropdown();
    };
    document.addEventListener("mousedown", onDocMouseDown, true);
    const onDocFocusIn = e => {
      if (!ddRef.current) return;
      const t = e.target;
      if (!(t instanceof Element)) return;
      const dd = ddRef.current;
      const inp = inputRef.current;
      if (!inp) return;
      if (inp === t || inp.contains(t)) return;
      if (dd.contains(t)) return;
      closeDropdown();
    };
    document.addEventListener("focusin", onDocFocusIn, true);
    const runLookup = (input, reason) => {
      const active = isAutocompleteActive();
      const q = (input.value || "").trim();
      if (!active) {
        acBlocked("client_config_flag", 'Smarty autocomplete is off in client config (addressAutocompleteAvailable). It comes from GET /api/config when SMARTY_AUTH_ID + SMARTY_AUTH_TOKEN are set on the server.', {
          reason,
          qLen: q.length,
          ...configSnapshot()
        });
        acDebug(id, "runLookup SKIP: address autocomplete not enabled in nitromuleReactData", {
          reason,
          qLen: q.length,
          ...configSnapshot()
        });
        closeDropdown();
        return;
      }
      if (q.length < MIN_CHARS) {
        acDebug(id, "runLookup SKIP: below MIN_CHARS (type at least 2 chars)", {
          reason,
          qLen: q.length,
          MIN_CHARS
        });
        closeDropdown();
        return;
      }
      acDebug(id, "runLookup FETCH", {
        reason,
        qPreview: q.slice(0, 36)
      });
      fetchLookup(q, "", input);
    };
    const schedule = () => {
      const input = inputRef.current;
      if (!input) {
        acDebug(id, "schedule SKIP: inputRef.current is null");
        return;
      }
      if (input !== boundEl) {
        acDebug(id, "schedule SKIP: input !== boundEl (not bound yet?)", {
          bound: !!boundEl,
          sameNode: boundEl === input
        });
        return;
      }
      if (debounceRef.current) clearTimeout(debounceRef.current);
      acDebug(id, "schedule debounce", DEBOUNCE_MS + "ms", {
        qLen: (input.value || "").trim().length
      });
      debounceRef.current = setTimeout(() => runLookup(input, "debounced-input"), DEBOUNCE_MS);
    };
    const onFocus = () => {
      const input = inputRef.current;
      if (!input || input !== boundEl) {
        acDebug(id, "onFocus SKIP", {
          hasInput: !!input,
          boundMatch: input === boundEl
        });
        return;
      }
      if (debounceRef.current) clearTimeout(debounceRef.current);
      requestAnimationFrame(() => {
        const el = inputRef.current;
        if (el && el === boundEl) runLookup(el, "focus");
      });
    };
    const onKeydown = e => {
      if (e.key === "Escape") closeDropdown();
    };
    const onBlur = () => {
      const commitIfFocusLeft = () => {
        if (!inputMountedRef.current) return;
        const ae = document.activeElement;
        const dd = ddRef.current;
        const inp = inputRef.current;
        if (dd && ae && dd.contains(ae)) return;
        if (inp && ae && (inp === ae || inp.contains(ae))) return;
        closeDropdown();
      };
      requestAnimationFrame(() => {
        commitIfFocusLeft();
        setTimeout(commitIfFocusLeft, 0);
      });
    };
    const bindIfNeeded = () => {
      pollTicks += 1;
      const el = inputRef.current;
      if (!el) {
        if (pollTicks === 1 || pollTicks % 20 === 0) {
          acDebug(id, "poll: still no inputRef.current", {
            pollTicks
          });
        }
        return;
      }
      if (el === boundEl) return;
      if (boundEl) {
        acDebug(id, "rebind: moving listeners to new input node");
        boundEl.removeEventListener("input", schedule);
        boundEl.removeEventListener("focus", onFocus);
        boundEl.removeEventListener("blur", onBlur);
        boundEl.removeEventListener("keydown", onKeydown);
      }
      boundEl = el;
      el.addEventListener("input", schedule);
      el.addEventListener("focus", onFocus);
      el.addEventListener("blur", onBlur);
      el.addEventListener("keydown", onKeydown);
      acDebug(id, "BOUND input", {
        pollTicks,
        qLen: (el.value || "").trim().length,
        ...configSnapshot()
      });
    };
    const onConfig = () => {
      acDebug(id, "event nitromule-config", {
        ...configSnapshot()
      });
      bindIfNeeded();
      const el = inputRef.current;
      const v = (el?.value || "").trim();
      if (el && isAutocompleteActive() && v.length >= MIN_CHARS) {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        runLookup(el, "nitromule-config");
      } else if (el) {
        acDebug(id, "nitromule-config: no immediate lookup", {
          vLen: v.length,
          active: isAutocompleteActive()
        });
      }
    };
    window.addEventListener("nitromule-config", onConfig);
    pollId = window.setInterval(bindIfNeeded, POLL_MS);
    bindIfNeeded();
    requestAnimationFrame(() => {
      bindIfNeeded();
      const el = inputRef.current;
      if (el && document.activeElement === el) {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        runLookup(el, "post-bind-active");
      }
    });
    return () => {
      acDebug(id, "effect cleanup");
      inputMountedRef.current = false;
      if (pollId != null) clearInterval(pollId);
      window.removeEventListener("nitromule-config", onConfig);
      document.removeEventListener("mousedown", onDocMouseDown, true);
      document.removeEventListener("focusin", onDocFocusIn, true);
      if (boundEl) {
        boundEl.removeEventListener("input", schedule);
        boundEl.removeEventListener("focus", onFocus);
        boundEl.removeEventListener("blur", onBlur);
        boundEl.removeEventListener("keydown", onKeydown);
      }
      if (debounceRef.current) clearTimeout(debounceRef.current);
      ddRef.current?._cleanup?.();
      closeDropdown();
    };
  }, [disabled, fetchLookup, closeDropdown, inputRef]);
  return null;
}
